import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getManagedAccountIds} from 'src/openapi/admin/apis/GetManagedAccountsApi';
import {runAccountSearch} from 'src/openapi/admin/apis/RunAccountSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {accountSearchResultStateSelector} from '../slices/account-search-result-state.slice';
import {
    accountSearchResultActions,
    accountSearchResultDataSelector,
    accountSearchResultResultSelector,
} from '../slices/account-search-result.slice';

import type {AccountSearchResultCacheKeys} from '../typedef';
import type {AccountSearchFiltering} from 'src/openapi/admin/models/AccountSearchFiltering';
import type {AccountSearchResponse} from 'src/openapi/admin/models/AccountSearchResponse';
import type {GetManagedAccountIdsRequestFilters} from 'src/openapi/admin/models/GetManagedAccountIdsRequestFilters';
import type {ErrorPayload, SuccessPayload} from 'src/typedef';


export const useGetAccountSearchResult = () => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback(async (cacheKeys: AccountSearchResultCacheKeys) => {
        const {searchCriteria, filters: {
            salesforceIntegrations,
            msDynamicIntegrations,
            dealCloudIntegrations,
            hubSpotIntegrations,
            entityType,
            ssoEnabled,
            ssoMandatory,
            ...managersFilters
        }} = cacheKeys;

        const accountSearchFilters = {
            salesforceIntegrations,
            msDynamicIntegrations,
            dealCloudIntegrations,
            hubSpotIntegrations,
            entityType,
            ssoEnabled,
            ssoMandatory,
        };

        if (!searchCriteria.accountManagerId) {
            return Promise.resolve({
                type: '',
                payload: {
                    data: new Error('Account manager id is not set'),
                    success: false,
                    error: true,
                } as ErrorPayload,
            });
        }

        const managedAccountIdsResponse = await apiDispatch(getManagedAccountIds({
            accountManagerId: searchCriteria.accountManagerId as number,
            getManagedAccountIdsRequest: {
                filters: managersFilters as GetManagedAccountIdsRequestFilters,
            },
        }));

        if (managedAccountIdsResponse.payload.success) {
            const {ids} = managedAccountIdsResponse.payload.data;

            if (ids.length) {
                const accountSearchResponse = await apiDispatch(runAccountSearch({
                    accountSearchRequest: {
                        searchCriteria: {accountIds: ids},
                        filtering: accountSearchFilters as AccountSearchFiltering,
                        sortCriteria: {
                            columnId: cacheKeys.sortCriteria.field,
                            direction: cacheKeys.sortCriteria.direction,
                        },
                        pagination: cacheKeys.pagination,
                    },
                }));

                if (accountSearchResponse.payload.success) {
                    return Promise.resolve({type: '', payload: accountSearchResponse.payload});
                } else {
                    return Promise.resolve({type: '', payload: accountSearchResponse.payload});
                }
            } else {
                return Promise.resolve({type: '', payload: {
                    data: {
                        result: [],
                        totalCount: 0,
                    },
                    success: true,
                    error: false,
                } as SuccessPayload<AccountSearchResponse>});
            }
        } else {
            return Promise.resolve({type: '', payload: managedAccountIdsResponse.payload});
        }
    }, [apiDispatch]);

    const setLoadedData = useCallback((
        response: AccountSearchResponse,
        cacheKeys: AccountSearchResultCacheKeys,
    ) => {
        dispatch(accountSearchResultActions.onSuccess(({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.result,
        })));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: accountSearchResultDataSelector,
        resultSelector: accountSearchResultResultSelector,
        stateSelector: accountSearchResultStateSelector,
        actions: accountSearchResultActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
